import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { Loading } from '@/shared/utils/vuex-loading'
import {
  CLEAR_OFFER,
  CREATE_OFFER,
  CREATE_OFFER_POST,
  DELETE_OFFER_POST,
  FETCH_DATA_FOR_OFFER_SETTINGS,
  FETCH_OFFER,
  FETCH_OFFER_PARTNERS,
  FETCH_OFFER_POSTS,
  FETCH_OFFERS,
  REMOVE_OFFER,
  SEARCH_OFFERS,
  UPDATE_OFFER,
  UPDATE_OFFER_POST,
} from '@/business/offers/store/types'
import {
  EmptyOfferSettingsViewModel,
  OfferSettingsViewModel,
} from '@/business/offers/models/offer-settings-view-model'
import { Api } from '@/api/api'
import {
  CreateOfferRequest,
  GetOffersRequest,
  LookupItem,
  OfferPageResponse,
  OfferResponse,
  PartnerPageResponse,
  PostPageResponse,
  PostResponse,
  UpdateOfferRequest,
} from '@/api/generated'
import { EmptyOfferViewModel, OfferViewModel } from '@/business/offers/models/offer-view-model'
import { FetchPagePayload } from '@/business/organisation/interfaces/fetch-page-payload'
import { EmptyOffersViewModel, OffersViewModel } from '@/business/offers/models/offers-view-model'
import { FetchOfferPosts } from '@/business/offers/store/models/fetch-offer-posts'
import { CreateOfferPostPayload } from '@/business/offers/store/models/create-offer-post-payload'
import { updateOrDiscard, updateOrPrepend, withoutEntry } from '@/shared/utils/list'
import { FetchOfferPartners } from '@/business/offers/store/models/fetch-offer-partners'
import { Post } from '@/business/post/models/post'
import { UpdateOfferPostPayload } from '@/business/offers/store/models/update-offer-post-payload'
import { DeleteOfferPostPayload } from '@/business/offers/store/models/delete-offer-post-payload'
import { mergePages } from '@/shared/utils/post-helpers/merge-pages'
import { sortBy } from '@/shared/utils/sort-by'
import { updatePostsPage } from '@/shared/utils/post-helpers/update-posts-page'
import { emptyPageResponse } from '@/shared/interfaces/page-response'

const STORE_NAME = 'offers'

@Module({
  name: STORE_NAME,
})
export class OffersModule extends VuexModule {
  public static readonly STORE_NAME = STORE_NAME

  public offer: OfferViewModel = EmptyOfferViewModel
  public offers: OffersViewModel = EmptyOffersViewModel
  public offerSettings: OfferSettingsViewModel = EmptyOfferSettingsViewModel

  @Mutation
  public [CLEAR_OFFER]() {
    this.offer = EmptyOfferViewModel
  }

  @Mutation
  public [CREATE_OFFER](offer: OfferResponse) {
    if (this.offerSettings !== EmptyOfferSettingsViewModel) {
      this.offerSettings.offer = offer
    }
  }

  @Mutation
  public [CREATE_OFFER_POST](post: PostResponse) {
    this.offer.posts = updatePostsPage(this.offer.posts, post, updateOrPrepend, 1)
  }

  @Mutation
  public [DELETE_OFFER_POST](post: Post) {
    this.offer.posts = updatePostsPage(this.offer.posts, post, withoutEntry, -1)
  }

  @Mutation
  public [FETCH_DATA_FOR_OFFER_SETTINGS](vm: OfferSettingsViewModel) {
    this.offerSettings = vm
  }

  @Mutation
  public [FETCH_OFFER](vm: OfferViewModel) {
    this.offer = vm
  }

  @Mutation
  public [FETCH_OFFERS](vm: OffersViewModel) {
    this.offers = vm
  }

  @Mutation
  public [FETCH_OFFER_PARTNERS](partnerPage: PartnerPageResponse) {
    this.offer.partners = partnerPage
  }

  @Mutation
  public [FETCH_OFFER_POSTS](posts: PostPageResponse) {
    this.offer.posts = mergePages(
      this.offer.posts,
      posts,
      sortBy(post => post.createdAt.getTime(), 'desc')
    )
  }

  @Mutation
  public [REMOVE_OFFER]() {
    this.offer = EmptyOfferViewModel
  }

  @Mutation
  public [SEARCH_OFFERS](offers: OfferPageResponse) {
    this.offers.offers = offers
  }

  @Mutation
  public [UPDATE_OFFER](offer: OfferResponse) {
    this.offer.offer = offer

    if (this.offerSettings !== EmptyOfferSettingsViewModel) {
      this.offerSettings.offer = offer
    }
  }

  @Mutation
  public [UPDATE_OFFER_POST](post: Post) {
    this.offer.posts = updatePostsPage(this.offer.posts, post, updateOrDiscard)
  }

  @Loading(CREATE_OFFER)
  @Action({
    commit: CREATE_OFFER,
    rawError: true,
  })
  public createOffer(offer: CreateOfferRequest): Promise<OfferResponse> {
    return Api.Offers.createOffer(offer)
  }

  @Loading(CREATE_OFFER_POST)
  @Action({
    commit: CREATE_OFFER_POST,
    rawError: true,
  })
  public async createOfferPost({ offerId, post }: CreateOfferPostPayload): Promise<PostResponse> {
    return Api.Offers.createPost({
      offerId,
      postRequest: post,
    })
  }

  @Loading(DELETE_OFFER_POST)
  @Action({
    commit: DELETE_OFFER_POST,
    rawError: true,
  })
  deleteOfferPost(payload: DeleteOfferPostPayload): Promise<Post> {
    return Api.Posts.deletePost({
      postId: payload.post.id,
    }).then(() => payload.post)
  }

  @Loading(FETCH_OFFER)
  @Action({
    commit: FETCH_OFFER,
    rawError: true,
  })
  public fetchOffer(offerId: number): Promise<OfferViewModel> {
    const offer$ = Api.Offers.getOffer({
      offerId,
    })

    return offer$.then(offer => ({
      offer,
      partners: this.offer.partners,
      posts: emptyPageResponse(),
    }))
  }

  @Loading(FETCH_OFFER_PARTNERS)
  @Action({
    commit: FETCH_OFFER_PARTNERS,
    rawError: true,
  })
  public async fetchOfferPartners(payload: FetchOfferPartners): Promise<PartnerPageResponse> {
    const vm = this.offer

    return Api.Offers.getOfferPartners({
      offerId: vm.offer.id,
      page: payload.page,
      pageSize: payload.pageSize,
    })
  }

  @Loading(FETCH_OFFER_POSTS)
  @Action({
    commit: FETCH_OFFER_POSTS,
    rawError: true,
  })
  public async fetchOfferPosts(payload: FetchOfferPosts): Promise<PostPageResponse> {
    return Api.Offers.getPosts(payload)
  }

  @Loading(FETCH_OFFERS)
  @Action({
    commit: FETCH_OFFERS,
    rawError: true,
  })
  public async fetchOffers({ page, pageSize }: FetchPagePayload): Promise<OffersViewModel> {
    const categories$ = Api.Lookup.getCategories().catch(() => [])
    const offerTypes$ = Api.Lookup.getOfferTypes().catch(() => [])

    const offers$ = Api.Offers.getOffers({
      page,
      pageSize,
    })

    return Promise.all([categories$, offerTypes$, offers$]).then(
      ([categories, offerTypes, offers]) => {
        return {
          lookup: {
            categories,
            offerTypes,
          },
          offers,
        }
      }
    )
  }

  @Loading(REMOVE_OFFER)
  @Action({
    commit: REMOVE_OFFER,
    rawError: true,
  })
  public removeOffer(offerId: number): Promise<void> {
    return Api.Offers.deleteOffer({
      offerId,
    })
  }

  @Loading(SEARCH_OFFERS)
  @Action({
    commit: SEARCH_OFFERS,
    rawError: true,
  })
  public searchOffers(payload: GetOffersRequest): Promise<OfferPageResponse> {
    return Api.Offers.getOffers(payload)
  }

  @Loading(UPDATE_OFFER)
  @Action({
    commit: UPDATE_OFFER,
    rawError: true,
  })
  public updateOffer(payload: UpdateOfferRequest): Promise<OfferResponse> {
    let promise$: Promise<OfferResponse>

    if (payload.offerId !== undefined) {
      promise$ = Api.Offers.updateOffer(payload)
    } else {
      promise$ = Api.Offers.createOffer(payload)
    }

    return promise$
  }

  @Loading(FETCH_DATA_FOR_OFFER_SETTINGS)
  @Action({
    commit: FETCH_DATA_FOR_OFFER_SETTINGS,
    rawError: true,
  })
  public fetchDataForOfferSettings(payload: { offerId?: number }): Promise<OfferSettingsViewModel> {
    const categories$: Promise<LookupItem[]> = Api.Lookup.getCategories()
    const offerTypes$: Promise<LookupItem[]> = Api.Lookup.getOfferTypes()
    const organisationTypes$: Promise<LookupItem[]> = Api.Lookup.getOrganisationTypes()
    const sectors$: Promise<LookupItem[]> = Api.Lookup.getOrganisationSectors()

    const organisations$ = Api.Lookup.lookupOrganisations({
      query: undefined,
    })

    let offer$: Promise<OfferResponse>

    // Create a new offer
    if (payload.offerId !== undefined && isNaN(payload.offerId) === false) {
      offer$ = Api.Offers.getOffer({
        offerId: payload.offerId,
      })
    } else {
      const emptyOffer: any = {
        audiences: [],
        categories: [],
        partners: [],
        resources: [],
        visible: false,
      } as Partial<OfferResponse>

      offer$ = new Promise<OfferResponse>(resolve => resolve(emptyOffer))
    }

    return Promise.all([
      offer$,
      organisations$,
      categories$,
      offerTypes$,
      organisationTypes$,
      sectors$,
    ]).then(([offer, organisations, categories, offerTypes, organisationTypes, sectors]) => ({
      lookup: {
        categories,
        offerTypes,
        organisations,
        organisationTypes,
        sectors,
      },
      offer,
    }))
  }

  @Loading(UPDATE_OFFER_POST)
  @Action({
    commit: UPDATE_OFFER_POST,
    rawError: true,
  })
  public async updateOfferPost({ postId, post }: UpdateOfferPostPayload): Promise<Post> {
    return Api.Posts.updatePost({
      postId,
      postRequest: post,
    })
  }
}
